import React, { useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Car,
  TrendingUp,
  DollarSign,
  History,
  ArrowUpRight,
  ArrowDownRight,
  ShoppingCart,
  Store,
  ChevronLeft,
  MapPin,
  Gauge,
  SlidersHorizontal,
} from 'lucide-react';
import { BuyProduct } from './consultancy/BuyProduct';
import { SaleProduct } from './consultancy/SaleProduct';
import { VehicleDetailModal } from './consultancy/VehicleDetailModal';
import { DashboardCard } from './dashboard/DashboardCard';
import { ExportMenu, type ExportRow } from '../lib/exportReports';
import { PageSearchBar } from '../components/common/PageSearchBar';
import DateRangeFilter from '../components/common/DateRangeFilter';
import { useDateRange, filterByRange, formatRangeLabel, inRange } from '../lib/dateFilter';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import type { ConsultancyLedgerRow } from './consultancy/consultancyTypes';
import {
  useConsultancyBundle,
  readConsultancyBundle,
  mergeMonthlyRevenue,
  buildOverviewTrend,
  computeConsultancyEconomics,
  profitLossByMonth,
  type ConsultancyPersistedVehicle as Vehicle,
} from '../lib/consultancyStore';
import { useCanWrite } from '../lib/permissions';
import { CardMetric } from '../components/common/ResponsiveCardValue';
import { useTranslation } from '../lib/i18n';
import { useChartTheme } from '../lib/chartTheme';
import { adminEntryIso } from '../lib/workspaceAsOf';
import { readCompanyBanks } from '../lib/financeCatalogs';
import { resolveBankLabel, type PaymentVia } from '../lib/paymentVia';
import { paymentViaPersistFields } from '../components/common/PaymentViaFields';
import { mediaSrc } from '../lib/mediaUrl';

type VehicleStatusFilter = 'all' | Vehicle['status'];

type ConsultancySection = 'main' | 'buy' | 'sale' | 'vehicles' | 'investment' | 'revenue' | 'history' | 'profit' | 'loss';

function ConsultancySubPage({
  title,
  subtitle,
  onBack,
  dateFilter,
  children,
}: {
  title: string;
  subtitle?: string;
  onBack: () => void;
  dateFilter?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="app-page space-y-6">
      <header className="app-page-header flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <button type="button" onClick={onBack} className="app-btn-secondary inline-flex w-fit shrink-0 items-center gap-2 py-2 pl-3 pr-4 text-sm">
            <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
            Back
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-800">Consultancy</p>
            <h1 className="app-page-title mt-0.5">{title}</h1>
            {subtitle ? <p className="app-page-sub mt-1">{subtitle}</p> : null}
          </div>
        </div>
        {dateFilter ? <div className="w-full min-w-0 shrink-0 sm:w-auto sm:self-end">{dateFilter}</div> : null}
      </header>
      {children}
    </div>
  );
}

function vehicleStatusBadgeClass(status: Vehicle['status']) {
  if (status === 'available') return 'border-emerald-200 bg-emerald-50 text-emerald-900';
  if (status === 'sold') return 'border-zinc-200 bg-zinc-100 text-zinc-700';
  return 'border-amber-200 bg-amber-50 text-amber-950';
}

export function Consultancy({ setActiveItem }: { setActiveItem: (item: string) => void }) {
  const { t } = useTranslation();
  const chart = useChartTheme();
  const [activeSection, setActiveSection] = useState<ConsultancySection>('main');
  const [bundle, updateBundle] = useConsultancyBundle();
  const canWrite = useCanWrite('consultancy');
  const vehicles = bundle.vehicles;
  const dr = useDateRange('all');

  const dateFilterControl = (
    <DateRangeFilter
      start={dr.range.start}
      end={dr.range.end}
      preset={dr.preset}
      onStart={dr.setStart}
      onEnd={dr.setEnd}
      onPreset={dr.setPreset}
      onClear={dr.clear}
      compact
    />
  );

  // Auto-computed economics from real buys (vehicles) and sales (date-scoped).
  const econ = useMemo(() => computeConsultancyEconomics(bundle, dr.range), [bundle, dr.range]);
  const pl = useMemo(() => profitLossByMonth(bundle, dr.range), [bundle, dr.range]);

  const investmentDerived = useMemo(() => {
    const mKey = (iso: string) => {
      const d = new Date(iso);
      return Number.isFinite(d.getTime()) ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : '';
    };
    const buckets = [
      { type: 'Available stock', amount: econ.availableValue },
      { type: 'Sold (cost of goods)', amount: econ.cogs },
    ].filter((b) => b.amount > 0);
    const inv = new Map<string, number>();
    for (const v of bundle.vehicles) {
      if (!inRange(v.boughtAt, dr.range)) continue;
      const k = mKey(v.boughtAt || '');
      if (!k) continue;
      inv.set(k, (inv.get(k) || 0) + (Number(v.price) || 0));
    }
    const ret = new Map<string, number>();
    for (const s of bundle.sales) {
      if (!inRange(s.recordedAt, dr.range)) continue;
      const k = mKey(String(s.recordedAt));
      if (!k) continue;
      ret.set(k, (ret.get(k) || 0) + (parseFloat(String(s.sellingPrice ?? '')) || 0));
    }
    const keys = [...new Set([...inv.keys(), ...ret.keys()])].sort();
    const chartRows = keys.map((name) => ({ name, investment: Math.round(inv.get(name) || 0), returns: Math.round(ret.get(name) || 0) }));
    return { totalInvestment: econ.investment, returns: econ.revenue, buckets, chart: chartRows, duration: `${keys.length || 0} mo` };
  }, [bundle.vehicles, bundle.sales, econ.availableValue, econ.cogs, econ.investment, econ.revenue, dr.range]);

  const revenueDetails = useMemo(() => {
    const monthlyBreakdown = mergeMonthlyRevenue(bundle.monthlyRevenue, bundle.sales, dr.range);
    const totalRevenue = monthlyBreakdown.reduce((s, r) => s + r.amount, 0);
    return { totalRevenue, monthlyBreakdown };
  }, [bundle.monthlyRevenue, bundle.sales, dr.range]);

  const profitDetails = useMemo(() => {
    const monthlyProfit = pl.monthlyProfit;
    const totalProfit = econ.profit;
    return { totalProfit, monthlyProfit };
  }, [pl.monthlyProfit, econ.profit]);

  const lossDetails = useMemo(() => {
    const monthlyLoss = pl.monthlyLoss;
    const totalLoss = econ.loss;
    return { totalLoss, monthlyLoss };
  }, [pl.monthlyLoss, econ.loss]);

  const overviewTrendData = useMemo(
    () => buildOverviewTrend(revenueDetails.monthlyBreakdown, pl.monthlyProfit, pl.monthlyLoss),
    [revenueDetails.monthlyBreakdown, pl.monthlyProfit, pl.monthlyLoss]
  );

  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleStatusFilter, setVehicleStatusFilter] = useState<VehicleStatusFilter>('all');
  const [vehicleSort, setVehicleSort] = useState<
    'listed' | 'bought_desc' | 'bought_asc' | 'price_desc' | 'price_asc' | 'year_desc' | 'kms_asc'
  >('bought_desc');
  const [vehiclePriceMin, setVehiclePriceMin] = useState('');
  const [vehiclePriceMax, setVehiclePriceMax] = useState('');
  const [vehicleKmMax, setVehicleKmMax] = useState('');

  const [investmentSearch, setInvestmentSearch] = useState('');
  const [investmentSort, setInvestmentSort] = useState<'label' | 'amount_desc' | 'amount_asc'>('label');

  const [revenueSearch, setRevenueSearch] = useState('');
  const [revenueSort, setRevenueSort] = useState<'month' | 'amount_desc' | 'amount_asc'>('month');

  const [profitSearch, setProfitSearch] = useState('');
  const [profitSort, setProfitSort] = useState<'month' | 'amount_desc' | 'amount_asc'>('month');

  const [lossSearch, setLossSearch] = useState('');
  const [lossSort, setLossSort] = useState<'month' | 'amount_desc' | 'amount_asc'>('month');

  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledgerKind, setLedgerKind] = useState<'all' | 'sale'>('all');
  const [ledgerDateFrom, setLedgerDateFrom] = useState('');
  const [ledgerDateTo, setLedgerDateTo] = useState('');
  const [ledgerSort, setLedgerSort] = useState<'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc'>('date_desc');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);

  const selectedVehicle = useMemo(
    () => (selectedVehicleId ? vehicles.find((v) => v.id === selectedVehicleId) ?? null : null),
    [selectedVehicleId, vehicles]
  );
  const selectedVehicleSale = useMemo(
    () => (selectedVehicleId ? bundle.sales.find((s) => s.vehicleId === selectedVehicleId) ?? null : null),
    [selectedVehicleId, bundle.sales]
  );

  const handleBuySubmit = (data: Record<string, unknown>) => {
    if (!canWrite) return;
    const price = Math.max(0, parseFloat(String(data.amountInvested ?? '')) || 0);
    const images = Array.isArray(data.images) ? (data.images as string[]) : [];
    const pay = paymentViaPersistFields(
      {
        paymentVia: (data.paymentVia as PaymentVia | '') || '',
        companyBankId: String(data.companyBankId || ''),
        paidBy: String(data.paidBy || ''),
        cashAmount: String(data.cashAmount || ''),
        bankAmount: String(data.bankAmount || ''),
      },
      price
    );
    const bankLabel = resolveBankLabel(readCompanyBanks(), pay.companyBankId);
    const newVehicle: Vehicle = {
      id: crypto.randomUUID(),
      name: String(data.vehicleName || 'New acquisition'),
      model: String(data.chasisNumber || data.vehicleNumber || '—'),
      year: new Date().getFullYear(),
      status: 'available',
      price: price || 0,
      location: '',
      kms: Math.max(0, parseInt(String(data.kms ?? ''), 10) || 0),
      boughtAt: adminEntryIso(String(data.boughtAt || data.acquisitionDate || '')),
      images,
      imageUrl: images[0],
      vehicleNumber: String(data.vehicleNumber || ''),
      sellerName: String(data.sellerName || ''),
      chasisNumber: String(data.chasisNumber || ''),
      engineNumber: String(data.engineNumber || ''),
      repairs: String(data.repairs || ''),
      cBookNumber: String(data.cBookNumber || ''),
      dealRef: String(data.dealRef || ''),
      paymentVia: pay.paymentVia,
      companyBankId: pay.companyBankId,
      companyBankLabel: bankLabel || undefined,
      paidBy: pay.paidBy,
      cashAmount: pay.cashAmount,
      bankAmount: pay.bankAmount,
    };
    updateBundle((b) => ({ ...b, vehicles: [...b.vehicles, newVehicle] }));
    setActiveSection('main');
  };

  const handleSaleSubmit = (data: Record<string, unknown>) => {
    if (!canWrite) return;
    const vehicleId = String(data.vehicleId ?? '').trim();
    const snap = readConsultancyBundle();
    const stock = snap.vehicles.find((x) => x.id === vehicleId);
    if (!vehicleId || !stock) {
      window.alert('Choose a vehicle from your available stock. Buy / acquisition adds stock first.');
      return;
    }
    if (stock.status !== 'available') {
      window.alert('Only vehicles marked Available can be sold here. (Sold or maintenance lines are not listed.)');
      return;
    }
    const salePrice = Math.max(0, parseFloat(String(data.sellingPrice ?? '')) || 0);
    const pay = paymentViaPersistFields(
      {
        paymentVia: (data.paymentVia as PaymentVia | '') || '',
        companyBankId: String(data.companyBankId || ''),
        paidBy: String(data.paidBy || ''),
        cashAmount: String(data.cashAmount || ''),
        bankAmount: String(data.bankAmount || ''),
      },
      salePrice
    );
    const bankLabel = resolveBankLabel(readCompanyBanks(), pay.companyBankId);
    const row = {
      ...data,
      vehicleId,
      vehicleName: `${stock.name} · ${stock.model}`.trim(),
      acquisitionCostAtSale: stock.price,
      sellingPrice: salePrice,
      recordedAt: adminEntryIso(String(data.recordedAt || data.saleDate || '')),
      paymentVia: pay.paymentVia,
      companyBankId: pay.companyBankId,
      companyBankLabel: bankLabel || undefined,
      paidBy: pay.paidBy,
      cashAmount: pay.cashAmount,
      bankAmount: pay.bankAmount,
    };
    updateBundle((b) => ({
      ...b,
      vehicles: b.vehicles.map((v) => (v.id === vehicleId ? { ...v, status: 'sold' as const } : v)),
      sales: [row, ...b.sales],
    }));
    setActiveSection('main');
  };

  const inventoryStats = useMemo(() => {
    const available = vehicles.filter((v) => v.status === 'available');
    const sold = vehicles.filter((v) => v.status === 'sold').length;
    const maintenance = vehicles.filter((v) => v.status === 'maintenance').length;
    const listValue = available.reduce((s, v) => s + v.price, 0);
    return {
      availableCount: available.length,
      sold,
      maintenance,
      listValue,
      total: vehicles.length,
    };
  }, [vehicles]);

  const vehiclesChartData = useMemo(() => {
    const byBrand = new Map<string, number>();
    for (const v of vehicles) {
      if (v.status !== 'available') continue;
      const brand = v.name.split(/\s+/)[0] ?? v.name;
      byBrand.set(brand, (byBrand.get(brand) ?? 0) + 1);
    }
    const rows = [...byBrand.entries()].map(([name, available]) => ({ name, available }));
    return rows;
  }, [vehicles]);

  const mergedLedger = useMemo((): ConsultancyLedgerRow[] => {
    return filterByRange(bundle.sales, dr.range, (s) => s.recordedAt).map((s, i) => {
      const d = new Date(s.recordedAt);
      const buyer = String(s.customerName ?? '—');
      const line =
        s.vehicleId && s.vehicleName
          ? `Sale · ${s.vehicleName} → ${buyer}`
          : `Sale · ${buyer} (no stock link — record before inventory wiring)`;
      return {
        id: `sale-${i}-${s.recordedAt}`,
        kind: 'sale' as const,
        iso: d.toISOString(),
        dateLabel: d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }),
        transaction: line,
        amount: Math.max(0, parseFloat(String(s.sellingPrice ?? '')) || 0),
      };
    });
  }, [bundle.sales, dr.range]);

  const ledgerTotalInr = useMemo(() => mergedLedger.reduce((s, r) => s + r.amount, 0), [mergedLedger]);

  const filteredLedger = useMemo(() => {
    let rows = [...mergedLedger];
    const q = ledgerSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => `${r.dateLabel} ${r.transaction} ${r.amount}`.toLowerCase().includes(q));
    }
    if (ledgerKind !== 'all') {
      rows = rows.filter((r) => r.kind === ledgerKind);
    }
    const fromTs = ledgerDateFrom ? new Date(ledgerDateFrom).getTime() : null;
    const toTs = ledgerDateTo ? new Date(ledgerDateTo + 'T23:59:59.999Z').getTime() : null;
    if (fromTs != null && Number.isFinite(fromTs)) {
      rows = rows.filter((r) => new Date(r.iso).getTime() >= fromTs);
    }
    if (toTs != null && Number.isFinite(toTs)) {
      rows = rows.filter((r) => new Date(r.iso).getTime() <= toTs);
    }
    const sorted = [...rows];
    switch (ledgerSort) {
      case 'date_asc':
        sorted.sort((a, b) => new Date(a.iso).getTime() - new Date(b.iso).getTime());
        break;
      case 'amount_desc':
        sorted.sort((a, b) => b.amount - a.amount);
        break;
      case 'amount_asc':
        sorted.sort((a, b) => a.amount - b.amount);
        break;
      default:
        sorted.sort((a, b) => new Date(b.iso).getTime() - new Date(a.iso).getTime());
    }
    return sorted;
  }, [mergedLedger, ledgerSearch, ledgerKind, ledgerDateFrom, ledgerDateTo, ledgerSort]);

  const ledgerFilteredTotalInr = useMemo(() => filteredLedger.reduce((s, r) => s + r.amount, 0), [filteredLedger]);

  const investmentRowsFiltered = useMemo(() => {
    let rows = [...investmentDerived.buckets];
    const q = investmentSearch.trim().toLowerCase();
    if (q) rows = rows.filter((r) => r.type.toLowerCase().includes(q));
    const sorted = [...rows];
    switch (investmentSort) {
      case 'amount_desc':
        sorted.sort((a, b) => b.amount - a.amount);
        break;
      case 'amount_asc':
        sorted.sort((a, b) => a.amount - b.amount);
        break;
      default:
        sorted.sort((a, b) => a.type.localeCompare(b.type));
    }
    return sorted;
  }, [investmentSearch, investmentSort, investmentDerived.buckets]);

  const revenueRowsFiltered = useMemo(() => {
    let rows = revenueDetails.monthlyBreakdown.map((r) => ({ ...r }));
    const q = revenueSearch.trim().toLowerCase();
    if (q) rows = rows.filter((r) => `${r.month} ${r.amount}`.toLowerCase().includes(q));
    const sorted = [...rows];
    switch (revenueSort) {
      case 'amount_desc':
        sorted.sort((a, b) => b.amount - a.amount);
        break;
      case 'amount_asc':
        sorted.sort((a, b) => a.amount - b.amount);
        break;
      default:
        sorted.sort((a, b) => a.month.localeCompare(b.month));
    }
    return sorted;
  }, [revenueSearch, revenueSort, revenueDetails.monthlyBreakdown]);

  const profitRowsFiltered = useMemo(() => {
    let rows = profitDetails.monthlyProfit.map((r) => ({ ...r }));
    const q = profitSearch.trim().toLowerCase();
    if (q) rows = rows.filter((r) => `${r.month} ${r.profit}`.toLowerCase().includes(q));
    const sorted = [...rows];
    switch (profitSort) {
      case 'amount_desc':
        sorted.sort((a, b) => b.profit - a.profit);
        break;
      case 'amount_asc':
        sorted.sort((a, b) => a.profit - b.profit);
        break;
      default:
        sorted.sort((a, b) => a.month.localeCompare(b.month));
    }
    return sorted;
  }, [profitSearch, profitSort, profitDetails.monthlyProfit]);

  const lossRowsFiltered = useMemo(() => {
    let rows = lossDetails.monthlyLoss.map((r) => ({ ...r }));
    const q = lossSearch.trim().toLowerCase();
    if (q) rows = rows.filter((r) => `${r.month} ${r.loss}`.toLowerCase().includes(q));
    const sorted = [...rows];
    switch (lossSort) {
      case 'amount_desc':
        sorted.sort((a, b) => b.loss - a.loss);
        break;
      case 'amount_asc':
        sorted.sort((a, b) => a.loss - b.loss);
        break;
      default:
        sorted.sort((a, b) => a.month.localeCompare(b.month));
    }
    return sorted;
  }, [lossSearch, lossSort, lossDetails.monthlyLoss]);

  const overviewPeakRevenue = useMemo(() => {
    if (overviewTrendData.length === 0) return 0;
    return Math.max(...overviewTrendData.map((d) => d.revenue));
  }, [overviewTrendData]);

  const filteredVehicles = useMemo(() => {
    const q = vehicleSearch.trim().toLowerCase();
    const pMin = Math.max(0, parseFloat(vehiclePriceMin) || 0);
    const pMaxRaw = parseFloat(vehiclePriceMax);
    const pMax = Number.isFinite(pMaxRaw) && pMaxRaw > 0 ? pMaxRaw : Infinity;
    const kmMax = Math.max(0, parseFloat(vehicleKmMax) || 0);
    // Date range applies to purchase (boughtAt) date.
    let list = filterByRange(vehicles, dr.range, (v) => v.boughtAt);
    if (vehicleStatusFilter !== 'all') {
      list = list.filter((v) => v.status === vehicleStatusFilter);
    }
    if (q) {
      list = list.filter((v) =>
        `${v.name} ${v.model} ${v.year} ${v.status} ${v.price} ${v.location ?? ''} ${v.kms ?? ''}`.toLowerCase().includes(q)
      );
    }
    if (pMin > 0 || pMax < Infinity) {
      list = list.filter((v) => v.price >= pMin && v.price <= pMax);
    }
    if (kmMax > 0) {
      list = list.filter((v) => (v.kms ?? 0) <= kmMax);
    }
    const boughtTs = (v: Vehicle) => {
      const t = v.boughtAt ? new Date(v.boughtAt).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    };
    const sorted = [...list];
    switch (vehicleSort) {
      case 'bought_desc':
        sorted.sort((a, b) => boughtTs(b) - boughtTs(a));
        break;
      case 'bought_asc':
        sorted.sort((a, b) => boughtTs(a) - boughtTs(b));
        break;
      case 'price_desc':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'price_asc':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'year_desc':
        sorted.sort((a, b) => b.year - a.year);
        break;
      case 'kms_asc':
        sorted.sort((a, b) => (a.kms ?? 0) - (b.kms ?? 0));
        break;
      default:
        sorted.sort((a, b) => a.id.localeCompare(b.id));
    }
    return sorted;
  }, [vehicles, vehicleSearch, vehicleStatusFilter, vehicleSort, vehiclePriceMin, vehiclePriceMax, vehicleKmMax, dr.range]);

  const overviewExportRows: ExportRow[] = useMemo(
    () => [
      { Metric: 'Period', Value: formatRangeLabel(dr.range) },
      { Metric: 'Stock units (all)', Value: inventoryStats.total },
      { Metric: 'Available units', Value: inventoryStats.availableCount },
      { Metric: 'Available list value (₹)', Value: inventoryStats.listValue },
      { Metric: 'Buys in range', Value: econ.buyCount },
      { Metric: 'Investment in range (₹)', Value: Math.round(econ.investment) },
      { Metric: 'Sales in range', Value: econ.saleCount },
      { Metric: 'Revenue in range (₹)', Value: Math.round(econ.revenue) },
      { Metric: 'COGS in range (₹)', Value: Math.round(econ.cogs) },
      { Metric: 'Profit in range (₹)', Value: Math.round(econ.profit) },
      { Metric: 'Loss in range (₹)', Value: Math.round(econ.loss) },
      { Metric: 'Ledger rows in range', Value: mergedLedger.length },
    ],
    [dr.range, inventoryStats, econ, mergedLedger.length],
  );

  // Render specific sections
  if (activeSection === 'buy') {
    return <BuyProduct setActiveItem={setActiveItem} onBack={() => setActiveSection('main')} onSubmit={handleBuySubmit} />;
  }

  if (activeSection === 'sale') {
    return (
      <SaleProduct
        setActiveItem={setActiveItem}
        onBack={() => setActiveSection('main')}
        onSubmit={handleSaleSubmit}
        availableVehicles={vehicles.filter((v) => v.status === 'available')}
      />
    );
  }

  if (activeSection === 'investment') {
    const investmentData = investmentDerived.chart;
    const investmentExportRows: ExportRow[] = investmentRowsFiltered.map((r) => ({
      Bucket: r.type,
      'Amount (₹)': r.amount,
    }));

    return (
      <ConsultancySubPage
        title="Investment"
        subtitle={`Capital deployed and returns · ${formatRangeLabel(dr.range)}`}
        onBack={() => setActiveSection('main')}
        dateFilter={dateFilterControl}
      >
        <div className="grid grid-cols-2 gap-2 sm:gap-2.5 lg:grid-cols-4">
          <div className="app-card border-l-[3px] border-l-zinc-500 shadow-sm ring-1 ring-zinc-950/[0.03]">
            <div className="app-card-inner">
              <h2 className="app-section-title">Total investment</h2>
              <CardMetric
                value={`₹${investmentDerived.totalInvestment.toLocaleString('en-IN')}`}
                className="mt-2 text-zinc-900"
              />
            </div>
          </div>
          <div className="app-card relative overflow-hidden border-l-[3px] border-l-emerald-600 shadow-sm ring-1 ring-zinc-950/[0.03]">
            <div className="app-card-inner">
              <span className="absolute right-3 top-3 rounded-full bg-emerald-600/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-emerald-800 ring-1 ring-emerald-600/20">
                {investmentDerived.totalInvestment > 0
                  ? `${Math.round((investmentDerived.returns / investmentDerived.totalInvestment) * 100)}%`
                  : '—'}
              </span>
              <h2 className="app-section-title">Returns (revenue)</h2>
              <CardMetric
                value={`₹${investmentDerived.returns.toLocaleString('en-IN')}`}
                className="mt-2 text-zinc-900"
              />
            </div>
          </div>
          <div className="app-card border-l-[3px] border-l-teal-600 shadow-sm ring-1 ring-zinc-950/[0.03]">
            <div className="app-card-inner">
              <h2 className="app-section-title">Profit</h2>
              <CardMetric
                value={`₹${econ.grossProfit.toLocaleString('en-IN')}`}
                className={`mt-2 ${econ.grossProfit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}
              />
            </div>
          </div>
          <div className="app-card border-l-[3px] border-l-amber-500 shadow-sm ring-1 ring-zinc-950/[0.03]">
            <div className="app-card-inner">
              <h2 className="app-section-title">Filtered total</h2>
              <CardMetric
                value={`₹${investmentRowsFiltered.reduce((s, r) => s + r.amount, 0).toLocaleString('en-IN')}`}
                className="mt-2 text-zinc-900"
              />
              <p className="mt-1 text-xs text-zinc-500">{investmentRowsFiltered.length} bucket(s) in view</p>
            </div>
          </div>
        </div>

        <div className="app-card overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03]">
          <div className="flex flex-col gap-3 border-b border-zinc-200 bg-zinc-50/90 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-zinc-700">
              <SlidersHorizontal className="h-4 w-4 shrink-0 text-zinc-500" aria-hidden />
              <div>
                <h2 className="text-sm font-semibold text-zinc-900">Allocation buckets</h2>
                <p className="text-xs tabular-nums text-zinc-500">{investmentRowsFiltered.length} bucket(s)</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <PageSearchBar
                id="consultancy-investment-search"
                value={investmentSearch}
                onChange={setInvestmentSearch}
                placeholder="Filter buckets…"
                className="min-w-[10rem] max-w-full flex-1 sm:max-w-xs"
              />
              <select
                className="app-field w-full py-2 text-sm sm:w-44"
                aria-label="Sort buckets"
                value={investmentSort}
                onChange={(e) => setInvestmentSort(e.target.value as typeof investmentSort)}
              >
                <option value="label">Sort · Name A–Z</option>
                <option value="amount_desc">Sort · Amount high → low</option>
                <option value="amount_asc">Sort · Amount low → high</option>
              </select>
              <ExportMenu
                title="Consultancy Investment Buckets"
                excelFilename="consultancy-investment-buckets.xls"
                rows={investmentExportRows}
                className="shrink-0"
              />
            </div>
          </div>
          <div className="app-table-wrap border-0">
            <table className="app-table min-w-full">
              <thead>
                <tr>
                  <th>Bucket</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {investmentRowsFiltered.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="py-8 text-center text-sm text-zinc-500">
                      No allocation buckets saved yet.
                    </td>
                  </tr>
                ) : null}
                {investmentRowsFiltered.map((item, idx) => (
                  <tr key={`${item.type}-${idx}`} className="transition-colors hover:bg-emerald-50/40">
                    <td className="font-medium text-zinc-800">{item.type}</td>
                    <td className="text-right font-semibold tabular-nums text-zinc-900">₹{item.amount.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="app-card overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03]">
          <div className="border-b border-zinc-200 bg-gradient-to-r from-zinc-50/95 to-emerald-50/30 px-4 py-3 dark:border-zinc-700 dark:from-zinc-900 dark:to-emerald-950/30">
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Investment vs returns</h2>
            <p className="mt-0.5 text-xs tabular-nums text-zinc-500 dark:text-zinc-400">
              {investmentData.length} point{investmentData.length === 1 ? '' : 's'}
            </p>
          </div>
          <div className="app-chart-panel bg-white p-4 dark:bg-zinc-900">
            {investmentData.length === 0 ? (
              <p className="py-16 text-center text-sm text-zinc-500 dark:text-zinc-400">No investment time series stored yet.</p>
            ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={investmentData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="name" stroke={chart.axis} fontSize={11} tick={{ fill: chart.axis }} />
                <YAxis stroke={chart.axis} fontSize={11} tick={{ fill: chart.axis }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12, color: chart.axis }} />
                <Line
                  type="monotone"
                  dataKey="investment"
                  name="Deployed"
                  stroke={chart.positive}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5 }}
                />
                <Line
                  type="monotone"
                  dataKey="returns"
                  name="Returns"
                  stroke={chart.secondary}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>
      </ConsultancySubPage>
    );
  }

  if (activeSection === 'revenue') {
    const revenueExportRows: ExportRow[] = revenueRowsFiltered.map((r) => ({
      Month: r.month,
      'Amount (₹)': r.amount,
    }));

    return (
      <ConsultancySubPage
        title="Revenue"
        subtitle={`Revenue by month · ${formatRangeLabel(dr.range)}`}
        onBack={() => setActiveSection('main')}
        dateFilter={dateFilterControl}
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="app-card border-l-[3px] border-l-emerald-600 shadow-sm ring-1 ring-zinc-950/[0.03] lg:col-span-1">
            <div className="app-card-inner">
              <p className="app-section-title">Booked revenue</p>
              <CardMetric
                value={`₹${revenueDetails.totalRevenue.toLocaleString('en-IN')}`}
                className="mt-2 text-zinc-900"
                scale="hero"
              />
              <p className="mt-2 text-xs tabular-nums text-zinc-500">
                ~₹{Math.round(revenueDetails.totalRevenue / revenueDetails.monthlyBreakdown.length).toLocaleString('en-IN')}
                /mo blended
              </p>
              <p className="mt-3 text-xs font-semibold text-zinc-600">
                Visible rows: ₹{revenueRowsFiltered.reduce((s, r) => s + r.amount, 0).toLocaleString('en-IN')}
              </p>
            </div>
          </div>
          <div className="app-card overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03] lg:col-span-2">
            <div className="flex flex-col gap-3 border-b border-zinc-200 bg-zinc-50/90 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold text-zinc-900">Monthly breakdown</h2>
                <p className="mt-0.5 text-xs tabular-nums text-zinc-500">
                  {revenueRowsFiltered.length} of {revenueDetails.monthlyBreakdown.length} rows
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <PageSearchBar
                  id="consultancy-revenue-search"
                  value={revenueSearch}
                  onChange={setRevenueSearch}
                  placeholder="Search month…"
                  className="min-w-[8rem] max-w-full flex-1 sm:max-w-xs"
                />
                <select
                  className="app-field py-2 text-sm sm:w-48"
                  aria-label="Sort revenue rows"
                  value={revenueSort}
                  onChange={(e) => setRevenueSort(e.target.value as typeof revenueSort)}
                >
                  <option value="month">Sort · Month</option>
                  <option value="amount_desc">Sort · Amount high → low</option>
                  <option value="amount_asc">Sort · Amount low → high</option>
                </select>
                <ExportMenu
                  title="Consultancy Revenue By Month"
                  excelFilename="consultancy-revenue-by-month.xls"
                  rows={revenueExportRows}
                  className="shrink-0"
                />
              </div>
            </div>
            <div className="app-table-wrap border-0">
              <table className="app-table min-w-full">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {revenueRowsFiltered.map((item) => (
                    <tr key={item.month} className="transition-colors hover:bg-emerald-50/40">
                      <td className="font-medium text-zinc-800">{item.month}</td>
                      <td className="text-right font-semibold tabular-nums text-zinc-900">
                        ₹{item.amount.toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </ConsultancySubPage>
    );
  }

  if (activeSection === 'history') {
    const ledgerExportRows: ExportRow[] = filteredLedger.map((row) => ({
      Date: row.dateLabel,
      Kind: row.kind,
      Description: row.transaction,
      'Amount (₹)': row.amount,
    }));

    const clearLedgerFilters = () => {
      setLedgerSearch('');
      setLedgerKind('all');
      setLedgerDateFrom('');
      setLedgerDateTo('');
      setLedgerSort('date_desc');
    };

    return (
      <ConsultancySubPage
        title="Transaction history"
        subtitle={`Sales ledger · ${formatRangeLabel(dr.range)}`}
        onBack={() => setActiveSection('main')}
        dateFilter={dateFilterControl}
      >
        <div className="app-card overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03]">
          <div className="flex flex-col gap-3 border-b border-zinc-200 bg-zinc-50/90 px-4 py-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-semibold text-zinc-900">Ledger</h2>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold tabular-nums text-zinc-600 ring-1 ring-zinc-200">
                  {filteredLedger.length} / {mergedLedger.length} rows
                </span>
              </div>
              <p className="mt-1 text-xs tabular-nums text-zinc-500">
                In range ₹{ledgerTotalInr.toLocaleString('en-IN')} · In view ₹{ledgerFilteredTotalInr.toLocaleString('en-IN')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <ExportMenu
                title="Consultancy Ledger"
                excelFilename="consultancy-ledger.xls"
                rows={ledgerExportRows}
                className="shrink-0"
              />
            </div>
          </div>

          <div className="space-y-3 border-b border-zinc-200 bg-white px-4 py-3">
            <PageSearchBar
              id="consultancy-ledger-search"
              value={ledgerSearch}
              onChange={setLedgerSearch}
              placeholder="Search description, date, amount…"
              className="max-w-full"
            />
            <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
              <div className="flex flex-wrap gap-1.5">
                {(['all', 'sale'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setLedgerKind(k)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                      ledgerKind === k ? 'bg-zinc-900 text-white shadow-sm' : 'bg-zinc-100 text-zinc-700 ring-1 ring-zinc-200 hover:bg-zinc-50'
                    }`}
                  >
                    {k === 'all' ? 'All' : 'Sales only'}
                  </button>
                ))}
              </div>
              <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-3">
                <div>
                  <label className="app-label" htmlFor="consultancy-ledger-from">
                    From date
                  </label>
                  <input
                    id="consultancy-ledger-from"
                    type="date"
                    className="app-field py-2 text-sm"
                    value={ledgerDateFrom}
                    onChange={(e) => setLedgerDateFrom(e.target.value)}
                  />
                </div>
                <div>
                  <label className="app-label" htmlFor="consultancy-ledger-to">
                    To date
                  </label>
                  <input
                    id="consultancy-ledger-to"
                    type="date"
                    className="app-field py-2 text-sm"
                    value={ledgerDateTo}
                    onChange={(e) => setLedgerDateTo(e.target.value)}
                  />
                </div>
                <div>
                  <label className="app-label" htmlFor="consultancy-ledger-sort">
                    Sort
                  </label>
                  <select
                    id="consultancy-ledger-sort"
                    className="app-field py-2 text-sm"
                    value={ledgerSort}
                    onChange={(e) => setLedgerSort(e.target.value as typeof ledgerSort)}
                  >
                    <option value="date_desc">Newest first</option>
                    <option value="date_asc">Oldest first</option>
                    <option value="amount_desc">Amount high → low</option>
                    <option value="amount_asc">Amount low → high</option>
                  </select>
                </div>
              </div>
              <button type="button" onClick={clearLedgerFilters} className="app-btn-secondary py-2 text-xs lg:self-end">
                Clear filters
              </button>
            </div>
          </div>

          <div className="app-table-wrap border-0 shadow-none">
            <table className="app-table min-w-full">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Description</th>
                  <th className="text-right">Amount (₹)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filteredLedger.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-8 text-center text-sm text-zinc-500">
                      No rows match these filters.
                    </td>
                  </tr>
                ) : (
                  filteredLedger.map((item) => (
                    <tr key={item.id} className="transition-colors hover:bg-zinc-50/90">
                      <td className="whitespace-nowrap text-xs tabular-nums text-zinc-600">{item.dateLabel}</td>
                      <td className="text-zinc-800">
                        <span
                          className="mr-2 inline-block h-2 w-2 rounded-full bg-emerald-500 align-middle"
                          title="Sale"
                        />
                        {item.transaction}
                      </td>
                      <td className="text-right font-semibold tabular-nums text-zinc-900">₹{item.amount.toLocaleString('en-IN')}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </ConsultancySubPage>
    );
  }

  if (activeSection === 'profit') {
    const profitExportRows: ExportRow[] = profitRowsFiltered.map((r) => ({
      Month: r.month,
      'Profit (₹)': r.profit,
    }));

    return (
      <ConsultancySubPage
        title="Profit"
        subtitle={`Profitable months · ${formatRangeLabel(dr.range)}`}
        onBack={() => setActiveSection('main')}
        dateFilter={dateFilterControl}
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="app-card border-l-[3px] border-l-emerald-600 shadow-sm ring-1 ring-zinc-950/[0.03]">
            <div className="app-card-inner">
              <p className="app-section-title">Total profit</p>
              <CardMetric
                value={`₹${profitDetails.totalProfit.toLocaleString('en-IN')}`}
                className="mt-2 text-emerald-800"
                scale="hero"
              />
              <p className="mt-2 text-xs tabular-nums text-emerald-700/80">{profitDetails.monthlyProfit.length} periods in book</p>
              <p className="mt-2 text-xs font-semibold text-zinc-600">
                Visible: ₹{profitRowsFiltered.reduce((s, r) => s + r.profit, 0).toLocaleString('en-IN')}
              </p>
            </div>
          </div>
          <div className="app-card overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03] lg:col-span-2">
            <div className="flex flex-col gap-3 border-b border-zinc-200 bg-emerald-50/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-sm font-semibold text-zinc-900">By month</h2>
              <div className="flex flex-wrap items-center gap-2">
                <PageSearchBar
                  id="consultancy-profit-search"
                  value={profitSearch}
                  onChange={setProfitSearch}
                  placeholder="Search month…"
                  className="min-w-[8rem] max-w-full flex-1 sm:max-w-xs"
                />
                <select
                  className="app-field py-2 text-sm sm:w-48"
                  aria-label="Sort profit rows"
                  value={profitSort}
                  onChange={(e) => setProfitSort(e.target.value as typeof profitSort)}
                >
                  <option value="month">Sort · Month</option>
                  <option value="amount_desc">Sort · Profit high → low</option>
                  <option value="amount_asc">Sort · Profit low → high</option>
                </select>
                <ExportMenu
                  title="Consultancy Profit By Month"
                  excelFilename="consultancy-profit-by-month.xls"
                  rows={profitExportRows}
                  className="shrink-0"
                />
              </div>
            </div>
            <div className="app-table-wrap border-0">
              <table className="app-table min-w-full">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="text-right">Profit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {profitRowsFiltered.map((item) => (
                    <tr key={item.month} className="transition-colors hover:bg-emerald-50/35">
                      <td className="font-medium text-zinc-800">{item.month}</td>
                      <td className="text-right font-semibold tabular-nums text-emerald-800">
                        ₹{item.profit.toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </ConsultancySubPage>
    );
  }

  if (activeSection === 'loss') {
    const lossExportRows: ExportRow[] = lossRowsFiltered.map((r) => ({
      Month: r.month,
      'Loss (₹)': r.loss,
    }));

    return (
      <ConsultancySubPage
        title="Loss"
        subtitle={`Loss-making months · ${formatRangeLabel(dr.range)}`}
        onBack={() => setActiveSection('main')}
        dateFilter={dateFilterControl}
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="app-card border-l-[3px] border-l-rose-600 shadow-sm ring-1 ring-zinc-950/[0.03]">
            <div className="app-card-inner">
              <p className="app-section-title">Total loss</p>
              <CardMetric
                value={`₹${lossDetails.totalLoss.toLocaleString('en-IN')}`}
                className="mt-2 text-rose-700"
                scale="hero"
              />
              <p className="mt-2 text-xs tabular-nums text-rose-600/90">{lossDetails.monthlyLoss.length} periods in book</p>
              <p className="mt-2 text-xs font-semibold text-zinc-600">
                Visible: ₹{lossRowsFiltered.reduce((s, r) => s + r.loss, 0).toLocaleString('en-IN')}
              </p>
            </div>
          </div>
          <div className="app-card overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03] lg:col-span-2">
            <div className="flex flex-col gap-3 border-b border-zinc-200 bg-rose-50/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-sm font-semibold text-zinc-900">By month</h2>
              <div className="flex flex-wrap items-center gap-2">
                <PageSearchBar
                  id="consultancy-loss-search"
                  value={lossSearch}
                  onChange={setLossSearch}
                  placeholder="Search month…"
                  className="min-w-[8rem] max-w-full flex-1 sm:max-w-xs"
                />
                <select
                  className="app-field py-2 text-sm sm:w-48"
                  aria-label="Sort loss rows"
                  value={lossSort}
                  onChange={(e) => setLossSort(e.target.value as typeof lossSort)}
                >
                  <option value="month">Sort · Month</option>
                  <option value="amount_desc">Sort · Loss high → low</option>
                  <option value="amount_asc">Sort · Loss low → high</option>
                </select>
                <ExportMenu
                  title="Consultancy Loss By Month"
                  excelFilename="consultancy-loss-by-month.xls"
                  rows={lossExportRows}
                  className="shrink-0"
                />
              </div>
            </div>
            <div className="app-table-wrap border-0">
              <table className="app-table min-w-full">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="text-right">Loss</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {lossRowsFiltered.map((item) => (
                    <tr key={item.month} className="transition-colors hover:bg-rose-50/40">
                      <td className="font-medium text-zinc-800">{item.month}</td>
                      <td className="text-right font-semibold tabular-nums text-rose-700">
                        ₹{item.loss.toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </ConsultancySubPage>
    );
  }

  if (activeSection === 'vehicles') {
    const rangeReady = filteredVehicles.filter((v) => v.status === 'available');
    const rangeSold = filteredVehicles.filter((v) => v.status === 'sold').length;
    const rangeService = filteredVehicles.filter((v) => v.status === 'maintenance').length;
    const rangeListValue = rangeReady.reduce((s, v) => s + v.price, 0);
    const rangeAvg =
      rangeReady.length > 0 ? Math.round(rangeListValue / rangeReady.length) : 0;

    const clearVehicleFilters = () => {
      setVehicleSearch('');
      setVehicleStatusFilter('all');
      setVehicleSort('bought_desc');
      setVehiclePriceMin('');
      setVehiclePriceMax('');
      setVehicleKmMax('');
    };

    const vehicleExportRows: ExportRow[] = filteredVehicles.map((v) => ({
      Name: v.name,
      Year: v.year,
      Status: v.status,
      Price: v.price,
      Kms: v.kms,
      Location: v.location,
      Bought: v.boughtAt
        ? new Date(v.boughtAt).toLocaleDateString('en-IN', { dateStyle: 'medium' })
        : '—',
    }));

    return (
      <ConsultancySubPage
        title="Vehicle inventory"
        subtitle={`Stock bought in period · ${formatRangeLabel(dr.range)}`}
        onBack={() => setActiveSection('main')}
        dateFilter={dateFilterControl}
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
          <div className="app-card border-l-[3px] border-l-emerald-600 p-3 sm:p-3.5">
            <p className="app-section-title">Ready</p>
            <CardMetric value={String(rangeReady.length)} className="mt-1 text-zinc-950" />
          </div>
          <div className="app-card border-l-[3px] border-l-zinc-400 p-3 sm:p-3.5">
            <p className="app-section-title">List value (ready)</p>
            <CardMetric
              value={`₹${rangeListValue.toLocaleString('en-IN')}`}
              className="mt-1 text-zinc-950"
              scale="compact"
            />
          </div>
          <div className="app-card border-l-[3px] border-l-zinc-500 p-3 sm:p-3.5">
            <p className="app-section-title">Sold</p>
            <CardMetric value={String(rangeSold)} className="mt-1 text-zinc-900" />
          </div>
          <div className="app-card border-l-[3px] border-l-amber-500 p-3 sm:p-3.5">
            <p className="app-section-title">Service</p>
            <CardMetric value={String(rangeService)} className="mt-1 text-zinc-900" />
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <span className="inline-flex items-center rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-xs font-semibold tabular-nums text-zinc-600 shadow-sm">
            {filteredVehicles.length} in range
          </span>
          <span className="inline-flex items-center rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-xs font-semibold tabular-nums text-zinc-600 shadow-sm">
            Avg list ₹{rangeAvg > 0 ? rangeAvg.toLocaleString('en-IN') : '—'}
          </span>
        </div>

        <div className="app-card app-card-inner space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-zinc-700">
            <SlidersHorizontal className="h-4 w-4 text-zinc-500" aria-hidden />
            <span className="text-sm font-semibold text-zinc-900">Filters and sort</span>
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-4">
              <PageSearchBar
                id="consultancy-vehicle-search"
                value={vehicleSearch}
                onChange={setVehicleSearch}
                placeholder="Search make, model, yard, VIN hints…"
                className="max-w-full"
              />
            </div>
            <div className="flex flex-wrap gap-1.5 lg:col-span-4">
              {(['all', 'available', 'sold', 'maintenance'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setVehicleStatusFilter(f)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                    vehicleStatusFilter === f
                      ? 'bg-emerald-800 text-white shadow-sm'
                      : 'bg-zinc-100 text-zinc-700 ring-1 ring-zinc-200 hover:bg-zinc-50'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'maintenance' ? 'Service' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:col-span-4">
              <div>
                <label className="app-label" htmlFor="veh-price-min">
                  Min ₹
                </label>
                <input
                  id="veh-price-min"
                  type="number"
                  min={0}
                  className="app-field py-2 text-sm"
                  placeholder="0"
                  value={vehiclePriceMin}
                  onChange={(e) => setVehiclePriceMin(e.target.value)}
                />
              </div>
              <div>
                <label className="app-label" htmlFor="veh-price-max">
                  Max ₹
                </label>
                <input
                  id="veh-price-max"
                  type="number"
                  min={0}
                  className="app-field py-2 text-sm"
                  placeholder="Any"
                  value={vehiclePriceMax}
                  onChange={(e) => setVehiclePriceMax(e.target.value)}
                />
              </div>
              <div>
                <label className="app-label" htmlFor="veh-km-max">
                  Max km
                </label>
                <input
                  id="veh-km-max"
                  type="number"
                  min={0}
                  className="app-field py-2 text-sm"
                  placeholder="Any"
                  value={vehicleKmMax}
                  onChange={(e) => setVehicleKmMax(e.target.value)}
                />
              </div>
              <div>
                <label className="app-label" htmlFor="consultancy-vehicle-sort">
                  Sort
                </label>
                <select
                  id="consultancy-vehicle-sort"
                  className="app-field w-full py-2 text-sm"
                  aria-label="Sort vehicles"
                  value={vehicleSort}
                  onChange={(e) => setVehicleSort(e.target.value as typeof vehicleSort)}
                >
                  <option value="bought_desc">Bought · Newest first</option>
                  <option value="bought_asc">Bought · Oldest first</option>
                  <option value="listed">Listed order</option>
                  <option value="price_desc">Price high → low</option>
                  <option value="price_asc">Price low → high</option>
                  <option value="year_desc">Year (newest)</option>
                  <option value="kms_asc">Km (low first)</option>
                </select>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-zinc-100 pt-3">
            <ExportMenu
              title={`Consultancy Vehicles (${formatRangeLabel(dr.range)})`}
              excelFilename="consultancy-vehicles.xls"
              rows={vehicleExportRows}
              className="shrink-0"
            />
            <button type="button" onClick={clearVehicleFilters} className="app-btn-secondary py-2 text-xs">
              Clear filters
            </button>
          </div>
        </div>

        {filteredVehicles.length === 0 ? (
          <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50/80 py-10 text-center text-sm text-zinc-600">
            No matches
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filteredVehicles.map((vehicle) => (
              <article
                key={vehicle.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedVehicleId(vehicle.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedVehicleId(vehicle.id);
                  }
                }}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-zinc-200/90 bg-white shadow-md ring-1 ring-zinc-950/[0.04] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-zinc-100">
                  {vehicle.imageUrl ? (
                    <img
                      src={mediaSrc(vehicle.imageUrl)}
                      alt={`${vehicle.name} ${vehicle.year}`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-zinc-400">No photo</div>
                  )}
                  <div
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/55 to-transparent"
                    aria-hidden
                  />
                  <span
                    className={`absolute right-2 top-2 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase shadow-md backdrop-blur-md ${vehicleStatusBadgeClass(vehicle.status)}`}
                  >
                    {vehicle.status}
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-3 p-4">
                  <div>
                    <h2 className="text-base font-semibold leading-snug text-zinc-950">{vehicle.name}</h2>
                    <p className="mt-0.5 text-xs text-zinc-500">
                      {vehicle.model} · {vehicle.year}
                    </p>
                  </div>
                  <dl className="grid grid-cols-1 gap-2.5 text-xs text-zinc-600 sm:grid-cols-2">
                    {vehicle.location ? (
                      <div className="flex gap-2">
                        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-400" aria-hidden />
                        <span className="leading-snug">{vehicle.location}</span>
                      </div>
                    ) : null}
                    <div className="flex gap-2">
                      <Gauge className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-400" aria-hidden />
                      <span>{vehicle.kms != null ? `${vehicle.kms.toLocaleString('en-IN')} km` : '—'}</span>
                    </div>
                  </dl>
                  <div className="mt-auto border-t border-zinc-100 pt-3">
                    <CardMetric
                      value={`₹${vehicle.price.toLocaleString('en-IN')}`}
                      className="text-zinc-950"
                      scale="compact"
                    />
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
        {selectedVehicle ? (
          <VehicleDetailModal
            vehicle={selectedVehicle}
            sale={selectedVehicleSale}
            onClose={() => setSelectedVehicleId(null)}
          />
        ) : null}
      </ConsultancySubPage>
    );
  }

  const cards: {
    titleKey: 'consultancy.vehicles' | 'consultancy.investment' | 'consultancy.revenue' | 'consultancy.history' | 'consultancy.profit' | 'consultancy.loss' | 'consultancy.buyStock' | 'consultancy.sellStock';
    icon: LucideIcon;
    onClick: () => void;
    borderAccent: string;
    iconBg: string;
    iconColor: string;
    status?: string;
    requiresWrite?: boolean;
  }[] = [
    {
      titleKey: 'consultancy.vehicles',
      icon: Car,
      onClick: () => setActiveSection('vehicles'),
      borderAccent: 'border-l-zinc-500',
      iconBg: 'bg-zinc-50',
      iconColor: 'text-zinc-700',
      status: `${inventoryStats.availableCount} · ₹${inventoryStats.listValue.toLocaleString('en-IN')}`,
    },
    {
      titleKey: 'consultancy.investment',
      icon: TrendingUp,
      onClick: () => setActiveSection('investment'),
      borderAccent: 'border-l-emerald-600',
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-700',
      status: `₹${investmentDerived.totalInvestment.toLocaleString('en-IN')}`,
    },
    {
      titleKey: 'consultancy.revenue',
      icon: DollarSign,
      onClick: () => setActiveSection('revenue'),
      borderAccent: 'border-l-teal-600',
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-700',
      status: `₹${revenueDetails.totalRevenue.toLocaleString('en-IN')}`,
    },
    {
      titleKey: 'consultancy.history',
      icon: History,
      onClick: () => setActiveSection('history'),
      borderAccent: 'border-l-zinc-400',
      iconBg: 'bg-zinc-50',
      iconColor: 'text-zinc-600',
      status: `${mergedLedger.length}`,
    },
    {
      titleKey: 'consultancy.profit',
      icon: ArrowUpRight,
      onClick: () => setActiveSection('profit'),
      borderAccent: 'border-l-emerald-600',
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-700',
      status: `+₹${profitDetails.totalProfit.toLocaleString('en-IN')}`,
    },
    {
      titleKey: 'consultancy.loss',
      icon: ArrowDownRight,
      onClick: () => setActiveSection('loss'),
      borderAccent: 'border-l-rose-600',
      iconBg: 'bg-rose-50',
      iconColor: 'text-rose-700',
      status: `−₹${lossDetails.totalLoss.toLocaleString('en-IN')}`,
    },
    {
      titleKey: 'consultancy.buyStock',
      icon: ShoppingCart,
      onClick: () => setActiveSection('buy'),
      borderAccent: 'border-l-emerald-700',
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-800',
      status: `${inventoryStats.total} unit${inventoryStats.total === 1 ? '' : 's'}`,
      requiresWrite: true,
    },
    {
      titleKey: 'consultancy.sellStock',
      icon: Store,
      onClick: () => setActiveSection('sale'),
      borderAccent: 'border-l-teal-700',
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-800',
      status: `${inventoryStats.availableCount} available`,
      requiresWrite: true,
    },
  ];

  return (
    <div className="app-page space-y-6">
      <header className="app-page-header flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-800">{t('consultancy.title')}</p>
          <h1 className="app-page-title mt-0.5">{t('consultancy.operationsDesk')}</h1>
          <p className="app-page-sub mt-1">
            Stock, sales, investment and profit · {formatRangeLabel(dr.range)}
          </p>
        </div>
        <div className="flex min-w-0 flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          {dateFilterControl}
          <ExportMenu
            title={`Consultancy Overview (${formatRangeLabel(dr.range)})`}
            excelFilename="consultancy-overview.xls"
            rows={overviewExportRows}
            className="shrink-0"
          />
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2 sm:gap-2.5 lg:grid-cols-4">
        {cards
          .filter((card) => canWrite || !card.requiresWrite)
          .map((card, index) => (
          <DashboardCard
            key={index}
            dense
            title={t(card.titleKey)}
            value={card.status ?? ''}
            icon={card.icon}
            accentTop={`border-l-[3px] ${card.borderAccent}`}
            iconColor={card.iconColor}
            onClick={card.onClick}
          />
        ))}
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:gap-4">
        <div className="app-card flex-1 overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.04]">
          <div className="border-b border-zinc-200 bg-gradient-to-r from-zinc-50/95 to-emerald-50/25 px-4 py-3 dark:border-zinc-700 dark:from-zinc-900 dark:to-emerald-950/25">
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Financial overview</h2>
            <p className="mt-0.5 text-xs tabular-nums text-zinc-500 dark:text-zinc-400">Peak rev · ₹{overviewPeakRevenue.toLocaleString('en-IN')}</p>
          </div>
          <div className="app-chart-panel bg-gradient-to-b from-white to-zinc-50/30 p-4 dark:from-zinc-900 dark:to-zinc-950/50">
            {overviewTrendData.length === 0 ? (
              <p className="py-16 text-center text-sm text-zinc-500 dark:text-zinc-400">
                No P&L trend points yet — record sales or add monthly rows in consultancy storage.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={overviewTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="name" stroke={chart.axis} fontSize={11} tick={{ fill: chart.axis }} />
                  <YAxis stroke={chart.axis} fontSize={11} tick={{ fill: chart.axis }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11, color: chart.axis }} />
                  <Line type="monotone" dataKey="profit" name="Profit" stroke={chart.positive} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="loss" name="Loss" stroke={chart.negative} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="revenue" name="Revenue" stroke={chart.secondary} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="app-card flex-1 overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.04]">
          <div className="border-b border-zinc-200 bg-gradient-to-r from-zinc-50/95 to-teal-50/20 px-4 py-3 dark:border-zinc-700 dark:from-zinc-900 dark:to-teal-950/25">
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Stock by brand</h2>
            <p className="mt-0.5 text-xs tabular-nums text-zinc-500 dark:text-zinc-400">
              Ready {inventoryStats.availableCount} · ₹{inventoryStats.listValue.toLocaleString('en-IN')}
            </p>
          </div>
          <div className="app-chart-panel bg-gradient-to-b from-white to-zinc-50/30 p-4 dark:from-zinc-900 dark:to-zinc-950/50">
            {vehiclesChartData.length === 0 ? (
              <p className="py-16 text-center text-sm text-zinc-500 dark:text-zinc-400">No ready-stock rows to chart — add available vehicles from the desk.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={vehiclesChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="name" stroke={chart.axis} fontSize={11} tick={{ fill: chart.axis }} />
                  <YAxis allowDecimals={false} stroke={chart.axis} fontSize={11} tick={{ fill: chart.axis }} />
                  <Tooltip formatter={(v: number) => [`${v} unit(s)`, 'Available']} />
                  <Legend wrapperStyle={{ fontSize: 11, color: chart.axis }} />
                  <Bar dataKey="available" name="Ready stock" fill={chart.primary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}